import { useEffect, useMemo, useState } from 'react';
import { fetchFinderEvents } from '../../api/events';
import { UnpopulatedEvent } from '../../utils/types';
import { useGeoData } from '../../hooks/geoData/useGeoData';
import { format, parseISO } from 'date-fns';
import { renderEventDate } from '../../utils/helpers';
import { useAppSelector } from '../../store/hooks';
import { useTranslation } from '../../hooks/translations/TranslationProvider';

export const useFinder = (
    countryCode: string,
    eventType?: string,
    city?: string,
    countryCodeForCitySearch?: string
) => {
    const PAGE_SIZE = 50;

    const auth = useAppSelector(state => state.auth);
    const language = auth.authData?.language;
    const { t } = useTranslation();

    const { isLoading: isGeoDataLoading, getCities, citiesData } = useGeoData();

    const [isLoading, setIsLoading] = useState(false);
    const [moreIsLoading, setMoreIsLoading] = useState(false);

    const [events, setEvents] = useState<UnpopulatedEvent[] | null>(null);
    const [hasMore, setHasMore] = useState(false);
    const [skip, setSkip] = useState(0);
    const [error, setError] = useState(null);
    const [emptyAllSearch, setEmptyAllSearch] = useState<string | null>(null);

    useEffect(() => {
        let isActive = true;

        async function fetchData() {
            try {
                setIsLoading(true);
                setError(null);
                const data = await fetchFinderEvents(
                    countryCode,
                    city!,
                    0,
                    PAGE_SIZE,
                    eventType
                );
                if (!isActive) {
                    return;
                }
                setEvents(data);
                setEmptyAllSearch(
                    !eventType && data.length === 0
                        ? JSON.stringify([countryCode, city])
                        : null
                );
                setSkip(data.length);
                setHasMore(data.length === PAGE_SIZE);
            } catch (error: any) {
                if (isActive) {
                    console.warn(error);
                    setError(error);
                }
            } finally {
                if (isActive) {
                    setIsLoading(false);
                }
            }
        }
        if (city) {
            fetchData();
        }

        return () => {
            isActive = false;
        };
    }, [countryCode, city, eventType]);

    const showLoadMoreButton = hasMore;

    const onShowMoreClick = async () => {
        try {
            setMoreIsLoading(true);
            const newEvents = await fetchFinderEvents(
                countryCode,
                city || '',
                skip,
                PAGE_SIZE,
                eventType
            );
            setEvents([...events!, ...newEvents]);
            setSkip(prev => prev + newEvents.length);
            setHasMore(newEvents.length === PAGE_SIZE);
        } catch (error: any) {
            console.warn(error);
            setError(error);
        } finally {
            setMoreIsLoading(false);
        }
    };

    const getCitiesHandler = async (initials: string) => {
        await getCities(countryCodeForCitySearch || countryCode, initials);
    };

    const groupedEvents = useMemo(() => {
        const list = Array.isArray(events) ? events : [];
        const byDay = new Map<string, typeof list>();

        const grouped: {
            key: string;
            label: string;
            events: UnpopulatedEvent[];
        }[] = [];

        list.forEach(event => {
            const d = parseISO(event.date);
            const key = format(d, 'yyyy-MM-dd');
            if (!byDay.has(key)) byDay.set(key, []);
            byDay.get(key)!.push(event);
        });

        byDay.forEach((dayEvents, key) => {
            const dayDate = parseISO(`${key}T00:00:00`);
            const label = renderEventDate(dayDate, language, {
                todayLabel: t('date.today'),
                showTime: false,
            });

            grouped.push({ key, label, events: dayEvents });
        });

        grouped.sort((a, b) => a.key.localeCompare(b.key));

        return grouped;
    }, [events, language, t]);

    const disableSportFilters =
        !isLoading &&
        !error &&
        !eventType &&
        emptyAllSearch === JSON.stringify([countryCode, city]);

    return {
        isLoading,
        isGeoDataLoading,
        events,
        groupedEvents,
        error,
        showLoadMoreButton,
        onShowMoreClick,
        moreIsLoading,
        getCitiesHandler,
        citiesData,
        disableSportFilters,
    };
};
